import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, Button, Typography } from "@mui/material";
import CCrud from "components/Molecules/Crud";
import Breadcrumb from "components/Atoms/SharedComponents/Breadcrumbs/Breadcrumb";
import { useThemePalette } from "common/hooks/theme_palette";
import FinancialStats from "./FinancialStats";
import FileCopyIcon from "@mui/icons-material/FileCopy";

function FinancialTemplate({
  data,
  count,
  paginationPage,
  handlePageChange,
  breadcrumbs,
  viewMode,
  onToggleView,
  statsData,
  filterObj,
  setFilterObj,
  transactionsList,
  transactionsCount,
  transactionsPage,
  handleTransactionsPageChange,
  filterList,
  isCollectionDisabled,
}) {
  const { t } = useTranslation();
  const { primary } = useThemePalette();

  const columnData = [
    { accessor: "id", Header: "#" },
    { accessor: "productName", Header: t("financial.product_name") },
    { accessor: "date", Header: t("financial.date") },
    { accessor: "orderNumber", Header: t("financial.order_number") },
    { accessor: "cadeauPercentage", Header: t("financial.cadeau_percentage") },
    { accessor: "amountDue", Header: t("financial.amount_due") },
  ];

  const transactionsColumnData = [
    { accessor: "id", Header: "#" },
    { accessor: "amount", Header: t("financial.amount") },
    { accessor: "notes", Header: t("financial.notes") },
    {
      accessor: "created_at",
      Header: t("financial.date"),
      Cell: ({ value }) => value?.split("T")[0],
    },
  ];

  return (
    <Box>
      <Stack
        direction="row"
        alignSelf="start"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        width="100%"
      >
        <Breadcrumb data={breadcrumbs} />
      </Stack>

      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        gap={2}
        mb={viewMode === "stats" ? 3 : 2}
      >
        <Typography fontSize="32px" fontWeight="700" fontFamily="Cairo" sx={{ mb: 0 }}>
          {viewMode === "stats" ? t("financial.collection") : t("financial.title")}
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
          {viewMode === "table" && (
            <Button
              variant="contained"
              onClick={() => onToggleView("stats")}
              disabled={isCollectionDisabled}
              sx={{
                backgroundColor: primary,
                color: "white",
                "&:hover": { backgroundColor: primary },
                borderRadius: "8px",
                textTransform: "none",
                "&.Mui-disabled": {
                  color: "white",
                  backgroundColor: "gray",
                },
              }}
            >
              {t("financial.collection")}
            </Button>
          )}

          <Button
            variant="contained"
            sx={{
              backgroundColor: primary,
              color: "white",
              "&:hover": { backgroundColor: primary },
              borderRadius: "8px",
              textTransform: "none",
            }}
            endIcon={<FileCopyIcon />}
          >
            {t("financial.contract_copy")}
          </Button>
        </Stack>
      </Stack>

      {viewMode === "table" ? (
        <CCrud
          table={{
            list: data,
            // listTitle: "Financial", // Or keep empty if you don't want a title inside the card
            columns: columnData,
            isSearch: false,
            isChecked: false,
            total_count: count,
            setFilterObj,
            filterList,
            className: "main-table",
            paginationPage: paginationPage,
            handlePageChange,
            rowData: () => {},
          }}
        />
      ) : (
        <FinancialStats data={statsData} />
      )}

      {viewMode === "stats" && (
        <Box mt={3}>
          <Typography
            fontSize="24px"
            fontWeight="700"
            fontFamily="Cairo"
            mb={2}
          >
            {t("financial.transactions")}
          </Typography>
          <CCrud
            table={{
              list: transactionsList,
              columns: transactionsColumnData,
              isSearch: false,
              isChecked: false,
              total_count: transactionsCount,
              paginationPage: transactionsPage,
              handlePageChange: handleTransactionsPageChange,
              rowData: () => {},
              setFilterObj: () => {},
            }}
          />
        </Box>
      )}
    </Box>
  );
}

export default FinancialTemplate;
